/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import CustomModalContainer from "../../../components/modals/CustomModalContainer";
import ModalInput from "../../ui/ModalTextInput";
import Dropdown from "../../ui/SelectDropdown";
import Button from "../../ui/Button";
import { RootState } from "../../../redux/app/store";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import {
  fetchCategoriesAsync,
  fetchSizeChartsAsync,
  updateProductDetailsAsync,
} from "../../../redux/features/ProductSlice";
import MultiSelectDropdown from "../../ui/MultiSelectDropdown";

export interface SizeItem {
  label: string;
  quantity: number;
}

export interface EditProductFormData {
  name: string;
  description: string;
  price: string;
  quantity: number;
  colors: string;
  sizeChartId: string;
  tags: string[];
  categoryId: string; // single string now
}

interface EditProductModalProps {
  open: boolean;
  onClose: () => void;
  product: any;
  sizeCharts?: Array<{ label: string; value: string }>;
  onSave: (updated: EditProductFormData) => void;
}

const EditProductModal: React.FC<EditProductModalProps> = ({
  open,
  onClose,
  product,
  onSave,
}) => {
  const { categories, sizeCharts, loading } = useAppSelector(
    (state: RootState) => state.products
  );
  const [formData, setFormData] = useState<EditProductFormData>({
    name: product?.name || "",
    description: product?.description || "",
    price: product?.price || "",
    quantity: product?.quantity || 0,
    colors: product?.colors || "",
    sizeChartId: product?.sizeChartId || "",
    tags: product?.tags || [],
    categoryId: product?.categoryIds?.id || "", // single string
  });
  const [selectedVariants, setSelectedVariants] = useState(
    product?.sizes?.map((s: any) => ({ label: s.label, value: s.label })) || []
  );

  const [errors, setErrors] = useState<Record<string, string>>({});
  const dispatch = useAppDispatch();

  // Get variants of selected size chart
  const variantsForSelectedSizeChart =
    sizeCharts?.items
      ?.find((sc: any) => sc.id === formData.sizeChartId)
      ?.variants?.map((v: any) => ({
        label: v.label,
        value: v.id,
      })) || [];

  // Combine all subcategories into one list
  const allSubcategories =
    categories
      ?.flatMap((cat: any) => cat.subcategories || [])
      ?.map((sub: any) => ({
        label: sub.title,
        value: sub.id,
      })) || [];
  // Combine all subcategories into one list
  const sizeChartByCategory =
    sizeCharts?.items?.map((sub: any) => ({
      label: sub.name,
      value: sub.id,
    })) || [];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const parsedValue = name === "quantity" ? Number(value) : value;

    setFormData({ ...formData, [name]: parsedValue });
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSave = async () => {
    const updatedData = {
      ...formData,
      sizes: selectedVariants.map((v: any) => ({ label: v.label })),
    };

    try {
      const updatedProduct = {
        ...formData,
        sizes: selectedVariants.map((v: any) => v.label),
      };
      await dispatch(
        updateProductDetailsAsync({ id: product?.id, updatedData })
      ).unwrap();

      onSave(updatedProduct);
      onClose();
    } catch (err) {
      console.error("Update failed:", err);
    }
  };

  useEffect(() => {
    if (!categories?.length) {
      dispatch(fetchCategoriesAsync());
    }
  }, [dispatch]);

  useEffect(() => {
    // Determine parent category id from the selected subcategory
    const parentCategoryId = categories?.find((cat: any) =>
      cat.subcategories?.some((sub: any) => sub.id === formData.categoryId)
    )?.id;

    if (parentCategoryId) {
      dispatch(
        fetchSizeChartsAsync({
          categoryId: parentCategoryId,
          page: 1,
          limit: 100,
        })
      );
    }
  }, [dispatch, formData.categoryId]);

  if (!open) return null;

  return (
    <CustomModalContainer
      title="Edit Product"
      open={open}
      onClose={onClose}
      footer={
        <div className="flex justify-between">
          <Button
            label="Cancel"
            onClick={onClose}
            customStyle={{
              minWidth: "120px",
              background: "none",
              color: "#000",
            }}
          />
          <Button
            label="Save"
            onClick={handleSave}
            customStyle={{ minWidth: "120px" }}
            isLoading={loading}
          />
        </div>
      }
    >
      <div className="p-6">
        <ModalInput
          label="Product Name"
          name="name"
          value={formData?.name}
          onChange={handleInputChange}
          required
          error={errors?.name}
        />

        <ModalInput
          label="Description"
          name="description"
          value={formData?.description}
          onChange={handleInputChange}
          required
          error={errors?.description}
        />

        {/* Subcategory Dropdown */}
        <Dropdown
          label="Category"
          name="categoryId"
          options={allSubcategories}
          value={formData.categoryId}
          onSelect={(value) =>
            setFormData({
              ...formData,
              categoryId: value,
            })
          }
          placeholder="Select a subcategory"
        />

        <Dropdown
          label="Size Chart Template"
          name="sizeChartId"
          options={sizeChartByCategory}
          value={formData?.sizeChartId}
          onSelect={(value) => setFormData({ ...formData, sizeChartId: value })}
          error={errors?.sizeChartId}
          placeholder="Select size chart"
        />

        {/* Sizes */}
        {formData.sizeChartId && variantsForSelectedSizeChart.length > 0 && (
          <MultiSelectDropdown
            label="Variants"
            placeholder="Select variants"
            options={variantsForSelectedSizeChart}
            selectedValues={selectedVariants}
            onSelect={setSelectedVariants}
          />
        )}

        {/* Tags */}
        <ModalInput
          label="Tags (comma separated)"
          name="tags"
          value={formData?.tags?.join(", ")}
          onChange={(e) =>
            setFormData({
              ...formData,
              tags: e.target.value.split(",").map((t) => t.trim()),
            })
          }
        />
      </div>
    </CustomModalContainer>
  );
};

export default EditProductModal;
